<template>
  <BasePaneCard
    heading="Perfil de recebimento de mensagens"
    headingTooltip="Analise a qualidade do recebimento<br>de mensagens da sua base."
    :isLoading="isLoading"
    :class="isLoading ? 'loading-height' : ''"
  >
    <div
      v-show="!isLoading"
      class="chats-sent"
      :class="{ 'chats-sent--disabled': checkPageFilterException(['csat', 'feeling']) }"
    >
      <template v-if="dataset">
        <BaseGrid id="upper-data">
          <BaseGrid class="grid-span-2">
            <div class="chats-sent__header">
              <div class="chats-sent__header__info">
                <span class="label label__upper">
                  Disparadas:
                </span>

                <span
                  class="value value__upper"
                  v-tooltip="{
                    theme: 'tooltip-top-right',
                    content: tooltipChatsSent
                  }"
                >
                  {{ (chatsSent || 0) | number }}
                </span>
              </div>

              <div class="chats-sent__header__info">
                <span class="label label__upper">
                  Taxa de resposta:
                </span>

                <span
                  class="value value__upper"
                  v-tooltip="{
                    theme: 'tooltip-top-left',
                    content: tooltipChatsResponseRate
                  }"
                >
                  {{ (chatsResponseRate * 100 || 0) | percentage('integer') }}
                </span>

                <span
                  class="value value__upper value__upper__percentage-change"
                  :class="chatsResponseRateChange >= 0
                    ? 'value__upper__percentage-change--positive'
                    : 'value__upper__percentage-change--negative'
                  "
                  v-tooltip="{
                    theme: 'tooltip-top-left',
                    content: tooltipChatsResponseRateChangePercentage
                  }"
                >
                  {{ (chatsResponseRateChange * 100 || 0) | percentage('integer') }}

                  <SvgIconTrendingUp v-if="chatsResponseRateChange >= 0" />
                  <SvgIconTrendingDown v-else />
                </span>
              </div>
            </div>
          </BaseGrid>
        </BaseGrid>

        <BaseGrid>
          <div class="div-chart">
            <ApexChart
              type="donut"
              height="170"
              :options="chart.chartOptions"
              :series="chart.series"
            />
            <div class="chart-center-text">{{ chatsSent | number }}</div>
          </div>
        </BaseGrid>

        <BaseGrid
          class="lower-row"
          v-tooltip="{
            theme: 'tooltip',
            content: tooltipChatsDelivered
          }"
        >
          <div>
            <span class="label label__lower label__lower--delivered">
              Entregues
            </span>
          </div>
          <div class="lower-right-row">
            <div class="div-chats-numeral">
              <span class="value value__lower value__lower__numeral">
                {{ chatsDelivered | number }}
              </span>
            </div>
            <div class="div-chats-percentage">
              <span class="value value__lower value__lower__percentage">
                {{ (chatsDeliveredPercentage * 100 || 0) | percentage('integer')  }}
              </span>
            </div>
          </div>
        </BaseGrid>
        <BaseGrid
          class="lower-row"
          v-tooltip="{
            theme: 'tooltip',
            content: tooltipChatsNotDeliveredReasons
          }"
        >
          <div>
            <span class="label label__lower label__lower--undelivered">
              Não entregues
            </span>
          </div>
          <div class="lower-right-row">
            <div class="div-chats-numeral">
              <span class="value value__lower value__lower__numeral">
                {{ chatsNotDelivered | number }}
              </span>
            </div>
            <div class="div-chats-percentage">
              <span class="value value__lower value__lower__percentage">
                {{ (chatsNotDeliveredPercentage * 100 || 0) | percentage('integer') }}
              </span>
            </div>
          </div>
        </BaseGrid>
      </template>

      <BaseEmptyState v-else>
        <SvgIconListX/>

        <h3>
          Ops! Algo de errado aconteceu.<br>
          Por favor, atualize a página.
        </h3>
      </BaseEmptyState>
    </div>
  </BasePaneCard>
</template>

<script>
import { numberFilter, percentageFilter, dateFilter } from '@/utils/filters'
import { pageFilterExceptionMixin } from '@/utils/mixins'
import BaseGrid from '@/components/base/BaseGrid'
import BasePaneCard from '@/components/base/BasePaneCard'
import BaseEmptyState from '@/components/base/BaseEmptyState'
import { mapState, mapGetters } from 'vuex'
import axios from 'axios'
import dayjs from 'dayjs'

export default {
  name: 'WidgetChatsSent',

  components: {
    BaseGrid,
    BasePaneCard,
    BaseEmptyState
  },

  filters: {
    number: numberFilter,
    percentage: percentageFilter,
    date: dateFilter
  },

  mixins: [
    pageFilterExceptionMixin
  ],

  data () {
    return {
      isLoading: false,
      dataset: null,
    }
  },

  computed: {
    ...mapState('base', [
      'mainApi',
      'pageFilter'
    ]),

    ...mapGetters('auth', [
      'userId',
      'platformId'
    ]),

    ...mapGetters('base', ['isAppliedPageFilter']),

    /*
      Current and Previous periods main objects
    */
    current () {
      return this.dataset?.chatsSentData?.find(item => item.period === 'current')
    },

    previous () {
      return this.dataset?.chatsSentData?.find(item => item.period === 'previous')
    },

    /*
      Current period data
    */
    chatsSent () {
      return this.current?.total_chats_sent
    },

    chatsResponses () {
      return this.current?.chats_answered
    },

    chatsResponseRate () {
      return this.calculatePercent(this.chatsSent, this.chatsResponses)
    },

    chatsResponseRateChange () {
      return this.chatsResponseRate - this.previousChatsResponseRate
    },

    chatsDelivered () {
      return this.current?.chats_delivered
    },

    chatsDeliveredPercentage () {
      return this.calculatePercent(this.chatsSent, this.chatsDelivered)
    },

    chatsDeliveredRead () {
      return this.current?.chats_read
    },

    chatsDeliveredReadPercentage () {
      return this.calculatePercent(this.chatsDelivered, this.chatsDeliveredRead)
    },

    chatsDeliveredUnread () {
      return this.current?.chats_unread
    },

    chatsDeliveredUnreadPercentage () {
      return this.calculatePercent(this.chatsDelivered, this.chatsDeliveredUnread)
    },

    chatsNotDelivered () {
      return this.current?.chats_not_delivered
    },

    chatsNotDeliveredPercentage () {
      return this.calculatePercent(this.chatsSent, this.chatsNotDelivered)
    },

    /*
      Previous period data
    */
    previousChatsSent () {
      return this.previous?.total_chats_sent
    },

    previousChatsResponses () {
      return this.previous?.chats_answered
    },

    previousChatsResponseRate () {
      return this.calculatePercent(this.previousChatsSent, this.previousChatsResponses)
    },

    /*
      Tooltip markdowns
    */
    tooltipChatsSent () {
      if (this.dataset?.error) return ''
      const [dateStart, dateEnd] = this.pageFilter?.dateRange

      const tooltipStartDate = dayjs(dateStart)
      const tooltipEndDate = dayjs(dateEnd)

      let markdown = `${this.formatDate(tooltipStartDate)} a ${this.formatDate(tooltipEndDate)}`
      markdown += `<br>${this.chatsSent} disparos`

      return markdown
    },

    tooltipChatsResponseRate () {
      if (this.dataset?.error) return ''
      const [dateStart, dateEnd] = this.pageFilter?.dateRange

      const tooltipStartDate = dayjs(dateStart)
      const tooltipEndDate = dayjs(dateEnd)

      let markdown = `${this.formatDate(tooltipStartDate)} a ${this.formatDate(tooltipEndDate)}`
      markdown += `<br>${this.chatsResponses.toLocaleString('pt-BR')} respostas (${(this.chatsResponseRate * 100).toFixed(0)}%)`

      return markdown
    },

    tooltipChatsResponseRateChangePercentage () {
      if (this.dataset?.error) return ''
      const [dateStart, dateEnd] = this.pageFilter?.dateRange

      const startDate = dayjs(dateStart)
      const endDate = dayjs(dateEnd)

      const previousStartDate = dayjs(this.previous?.date_range[0])
      const previousEndDate = dayjs(this.previous?.date_range[1])

      const UpOrDown = this.chatsResponseRateChange >= 0 ? 'Crescimento' : 'Diminuição'

      let markdown = `${this.formatDate(startDate)} a ${this.formatDate(endDate)}`
      markdown += `<br>${UpOrDown} de ${(Math.abs(this.chatsResponseRateChange * 100)).toFixed(0)}%`
      markdown += '<br>comparado ao período de'
      markdown += `<br>${this.formatDate(previousStartDate)} a ${this.formatDate(previousEndDate.subtract(1, 'day'))}`

      return markdown;
    },

    tooltipChatsDelivered () {
      if (this.dataset?.error) return ''

      let markdown = `${(this.chatsDeliveredReadPercentage * 100).toFixed(0)}% lidas`
      markdown += ` (${this.chatsDeliveredRead.toLocaleString('pt-BR')})`
      markdown += `<br>${(this.chatsDeliveredUnreadPercentage * 100).toFixed(0)}%`
      markdown += ` não lidas (${this.chatsDeliveredUnread.toLocaleString('pt-BR')})`

      return markdown
    },

    tooltipChatsNotDeliveredReasons () {
      if (this.dataset?.error) return ''
      const data = this.current?.chats_not_delivered_reasons

      let [dateStart] = this.pageFilter?.dateRange
      dateStart = dayjs(dateStart)

      let markdown = 'Nenhum dado disponível'

      // Find which key has the greatest number, and return the sum of others
      let arr = Object.entries(data)

      if (dateStart.isBefore(dayjs('2023-07-01'))) {
        markdown = 'Dados disponíveis a partir'
        markdown += '<br>de 1º de julho 2023'
      } else if (arr.length > 1) {
        arr.sort((a, b) => b[1] - a[1])
        let highest = arr[0]
        let otherSum = arr.slice(1).reduce((acc, curr) => acc + curr[1], 0)

        const total = highest[1] + otherSum

        markdown = `${highest[0]}: ${highest[1]} (${(highest[1] / total * 100).toFixed(0)}%)`
        markdown += `<br>Outros: ${otherSum} (${(otherSum / total * 100).toFixed(0)}%)`
      } else if (arr.length === 1) {
        const key = Object.keys(data)[0];
        const value = data[key];
        markdown = `${key}: ${value} (100%)`
      }

      return markdown
    },

    chart () {
      const isEmpty = !this.dataset ||
        this.dataset.error ||
        (this.chatsDelivered === 0 && this.chatsNotDelivered === 0)

      let apexChart = {
        series: isEmpty ? [1] : [this.chatsDelivered, this.chatsNotDelivered],
        chartOptions: {
          chart: {
            type: 'donut',
          },
          colors: isEmpty ? ['#ddd'] : ['#1FDE33', '#FF3737'],
          legend: {
            show: false,
          },
          dataLabels: {
            enabled: false,
          },
          plotOptions: {
            pie: {
              donut: {
                size: '85%', // donut's thickness
              },
              expandOnClick: false
            }
          },
          stroke: {
            show: false
          },
          tooltip: {
            enabled: !isEmpty,
            theme: 'dark', // style set at '::v-deep .apexcharts-tooltip'
            custom: ({series, seriesIndex, dataPointIndex, w}) => {
              if (seriesIndex === 0) return `<div>${this.tooltipChatsDelivered}</div>`;
              else return `<div>${this.tooltipChatsNotDeliveredReasons}</div>`;
            }
          }
        },
      }

      return apexChart
    }
  },

  watch: {
    pageFilter () {
      this.fetchData()
    }
  },

  mounted () {
    this.fetchData()
  },

  methods: {
    calculatePercent (base, rate) {
      if (base === 0 || isNaN(base) || isNaN(rate)) return null
      return rate / base
    },

    formatDate (dateString) {
      let date = new Date(dateString)

      let text = date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
      text = text.replaceAll(" de ", " ")

      return text
    },

    getFetchDataParams () {
      let params = ''
      let [dateStart, dateEnd] = this.pageFilter?.dateRange
      const { actions, plugin } = this.pageFilter

      if (!dateStart || !dateEnd) {
        dateStart = dayjs()
        dateEnd = dateStart.add(1, 'day')
      }

      params += `?firstDate=${dateFilter(dateStart, 'base')}&lastDate=${dateFilter(dateEnd, 'base')}`

      if (actions?.length) {
        params += `&actions=${actions.join()}`
      }

      if (plugin) {
        params += `&plugin=${plugin}`
      }

      return params
        .replace(/\?$/, '')
        .replace(/\?&/, '?')
    },

    async fetchData () {
      this.isLoading = true

      try {
        const params = this.getFetchDataParams()
        const metricUrl = `${this.mainApi}lead/message/chats-sent${params.replace(/date\/|action\//g, '')}`

        await axios
          .all([
            axios.get(metricUrl)
          ])
          .then(axios.spread((
            { data: response }
          ) => {
            this.dataset = {
              chatsSentData: response
            }
          }))
      } catch (error) {
        this.dataset = null

        throw error
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$blue-dark: nth($colors-custom, 1);
$blue-light: nth($colors-custom, 2);
$blue-light-2: nth($colors-custom, 7);
$gray-light: nth($colors-custom, 4);
$red: nth($colors-custom, 11);
$green: nth($colors-custom, 12);

.chats-sent {
  &--disabled {
    opacity: 0.3;
  }

  &__header {
    display: flex;
    justify-content: space-between;

    &__info {
      display: flex;
      align-items: center;
      padding-right: 5px;
    }
  }
}

* {
  font: {family: Montserrat;}
}

#upper-data {
  margin: 10px 0 0 0;
}

.upper-left-grid {
  display: flex;
  align-items: center;
}

.upper-right-grid {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: right;
}

.loading-height {
  height: 372px;
}

.lower-row {
  display: flex;
  align-items: center;
  padding: 0 10px;

  &:hover {
    background-color: rgba($gray-light, 0.5);
    border-radius: 20px;
  }
}

.lower-right-row {
  display: flex;
  justify-content: flex-end;
  flex-grow: 1;
}

.div-chats-numeral {
  flex-basis: 50px;
  text-align: right;
}

.div-chats-percentage {
  width: 50px;
  text-align: right;
}

.div-chart {
  height: 180px;
  background-color: rgba($gray-light, 0.5);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px 0;
}

.chart-center-text {
  position: absolute;
  pointer-events: none; /* This makes the mouse ignore the number, allowing interaction with the chart */
  z-index: 1;
  font: {
    size: 17px;
    weight: 700;
  }
}

::v-deep .apexcharts-tooltip {
  background-color: #000 !important; /* Replace with your desired color */
  padding: 8px;
  font: {
    family: Montserrat;
    size: 12.5px;
  }
  color: #fff !important;
}

.label {
  font: {
    weight: 400;
    color: $blue-dark;
  }

  &__upper {
    display: inline-block;
    font: {size: 12.5px;}
  }

  &__lower {
    font: {size: 14px;}

    &--delivered::before,
    &--undelivered::before {
      content: '';
      display: inline-block;
      width: 11px;
      height: 11px;
      border-radius: 50%;
      margin-right: 5px;
    }
    &--delivered::before {
      background-color: $green;
    }
    &--undelivered::before {
      background-color: $red;
    }
  }
}

.value {
  margin-left: 8px;
  font: {
    weight: 700;
    color: $blue-dark;
  }

  &__upper {
    display: inline-block;
    font: {
      weight: 700;
      size: 20px;
    }

    &__percentage-change {
      display: inline-block;

      font: {
        size: 14px;
        weight: 400;
      }

      &--positive {
        color: $green;
      }

      &--negative {
        color: $red;
      }
    }
  }

  &__lower {
    font: {
      size: 14px;
      weight: 700;
    }

    &__numeral {
      font: {
        size: 14px;
        weight: 600;
      }
    }

    &__percentage {
      font: {
        size: 14px;
        weight: 400;
      }
    }
  }
}
</style>
